import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed, effect, inject, input, output } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { StringBooleanMap } from '@bn/shared/entities/models';
import { BadgeComponent } from '@bn/web-ui/badge/badge.component';
import { FlagComponent } from '@bn/web-ui/flag';
import { NotAvailableComponent } from '@bn/web-ui/not-available';
import { PaginationComponent } from '@bn/web-ui/pagination';
import { TableSkeletonTemplateComponent } from '@bn/web-ui/standalone';
import { EVENT_LOG_DIMENSION_MAP, eventLogActionColor, eventLogValueLabel } from '../../../../models';
import { EventGroup } from '../../../../openapi';
import {
  AppShieldEventLogsSearchStore,
  EventLogGroupRows,
  EventLogGroupRowsRequest,
  groupKeyString,
  NESTED_PAGE_SIZE,
} from '../../../../stores/app-shield-event-logs-search.store';
import { EventLogsTableComponent } from '../event-logs-table/event-logs-table.component';

interface GroupKeyPart {
  dimension: string;
  label: string;
  value: string;
}

const SPARK_MIN_PCT = 8;

@Component({
  selector: 'app-app-shield-event-logs-groups-table',
  templateUrl: './event-logs-groups-table.component.html',
  styleUrls: ['./event-logs-groups-table.component.scss'],
  imports: [
    BadgeComponent,
    DatePipe,
    EventLogsTableComponent,
    FaIconComponent,
    FlagComponent,
    NgTemplateOutlet,
    NotAvailableComponent,
    PaginationComponent,
    TableSkeletonTemplateComponent,
  ],
})
export class EventLogsGroupsTableComponent {
  readonly groups = input<EventGroup[]>([]);
  readonly expandedGroupRows = input<Record<string, EventLogGroupRows | undefined>>({});
  readonly isLoading = input(false);

  readonly groupToggled = output<EventGroup>();
  readonly groupPageChange = output<EventLogGroupRowsRequest>();

  readonly #store = inject(AppShieldEventLogsSearchStore);

  readonly maxCount = computed(() => this.groups().reduce((max, group) => Math.max(max, group.count ?? 0), 0));

  readonly actionColor = eventLogActionColor;
  readonly nestedPageSize = NESTED_PAGE_SIZE;

  openRows: StringBooleanMap = {};

  constructor() {
    effect(() => {
      this.groups();
      this.openRows = {};
    });
  }

  keyOf(group: EventGroup): string {
    return groupKeyString(group.key);
  }

  keyParts(group: EventGroup): GroupKeyPart[] {
    return Object.entries(group.key ?? {}).map(([dim, value]) => ({
      dimension: dim,
      label: EVENT_LOG_DIMENSION_MAP[dim]?.shortLabel ?? dim,
      value: eventLogValueLabel(dim, value),
    }));
  }

  groupCountry(group: EventGroup): string {
    return (group.context?.['country'] as string) ?? '';
  }

  sparkBars(group: EventGroup): number[] | null {
    const values = group.sparkline;

    if (!values?.length) return null;

    const max = Math.max(1, ...values);

    return values.map((value) => (value === 0 ? 0 : Math.max(SPARK_MIN_PCT, (value / max) * 100)));
  }

  isHot(group: EventGroup): boolean {
    return this.maxCount() > 0 && (group.count ?? 0) >= this.maxCount();
  }

  isGroupLoading(key: string): boolean {
    return this.#store.loadingGroupKeys().includes(key);
  }

  onToggle(group: EventGroup): void {
    const key = this.keyOf(group);
    this.openRows[key] = !this.openRows[key];

    if (this.openRows[key] && !this.expandedGroupRows()[key]) this.groupToggled.emit(group);
  }

  onNestedPageChange(group: EventGroup, oneBasedPage: number): void {
    const page = oneBasedPage - 1;

    if (page === this.expandedGroupRows()[this.keyOf(group)]?.page) return;

    this.groupPageChange.emit({ group, page });
  }
}
